import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventService } from '../services/api.service';
import './EventPage.css';
import { FaCalendar, FaClock, FaMapMarkerAlt, FaTag, FaTicketAlt, FaCity } from 'react-icons/fa';

const EventPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [subEvents, setSubEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchEventAndDates = async () => {
            try {
                setLoading(true);
                setError(null);
                const eventData = await eventService.getEventById(id);
                setEvent(eventData);

                try {
                    const datesResponse = await eventService.getEventDates(id);
                    setSubEvents(datesResponse || []);
                } catch (subError) {
                    console.error('Error fetching sub-events:', subError);
                    setSubEvents([]);
                }
            } catch (error) {
                console.error('Failed to fetch event:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchEventAndDates();
    }, [id]);

    if (loading) return (
        <div className="loading-container">
            <div className="loading-spinner"></div>
        </div>
    );

    if (error) return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg w-full">
                <h2 className="text-xl font-semibold text-red-800 mb-2">Hata</h2>
                <p className="text-red-600 mb-4">{error}</p>
                <div className="text-gray-600 mb-4">
                    <p>Lütfen daha sonra tekrar deneyiniz veya destek ekibi ile iletişime geçiniz.</p>
                </div>
                <button 
                    onClick={() => window.location.reload()} 
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 mr-2"
                >
                    Tekrar Dene
                </button>
                <button 
                    onClick={() => navigate('/')} 
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                >
                    Ana Sayfaya Dön
                </button>
            </div>
        </div>
    );

    if (!event) return (
        <div className="event-not-found">
            <div className="error-container">
                <h2>Etkinlik bulunamadı</h2>
                <button onClick={() => navigate('/')}>Ana Sayfaya Dön</button>
            </div>
        </div>
    );

    // Safely transform event data
    const transformedEvent = {
        id: event._id || event.id,
        title: event.title || '',
        image_url: event.image_url,
        description: event.description || '',
        category: event.category || '',
        location: {
            venue: event.location?.venue || '',
            fullAddress: event.location ? 
                `${event.location.address || ''}, ${event.location.city || ''}` : ''
        }
    };

    return (
        <div className="event-page">
            <div className="event-container">
                <div className="event-header">
                    <img 
                        src={transformedEvent.image_url} 
                        alt={transformedEvent.title} 
                        className="event-banner"
                        onError={(e) => {
                            console.log('Image failed to load:', transformedEvent.image_url);
                            e.target.src = '/placeholder-event.jpg';
                        }}
                    />
                    <div className="event-overlay">
                        <h1>{event?.title}</h1>
                        <p className="event-description">{transformedEvent.description}</p>
                        
                        <div className="event-info-grid">
                            <div className="info-item">
                                <h3><FaTag /> Kategori</h3>
                                <p>{transformedEvent.category}</p>
                            </div>
                            {event.min_price && event.min_price.length > 0 && (
                                <div className="info-item">
                                    <h3><FaTicketAlt /> Fiyat</h3>
                                    <p>{Math.min(...event.min_price)} TL'den başlayan</p>
                                </div>
                            )}
                            {event.venues && event.venues.length > 0 && (
                                <div className="info-item">
                                    <h3><FaMapMarkerAlt /> Mekan</h3>
                                    <p>{event.venues.join(' • ')}</p>
                                </div>
                            )}
                            {event.total_sub_events && (
                                <div className="info-item">
                                    <h3><FaCalendar /> Toplam Gösterim</h3>
                                    <p>{event.total_sub_events} gösteri</p>
                                </div>
                            )}
                        </div>

                        {event.tags && event.tags.length > 0 && (
                            <div className="event-tags">
                                {event.tags.map((tag, index) => (
                                    <span key={index} className="event-tag">
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="sub-events-section">
                    <h2>Gösterim Tarihleri</h2>
                    {subEvents.length > 0 ? (
                        <div className="sub-events-grid">
                            {subEvents.map((subEvent, index) => (
                                <div key={subEvent.id || index} className="sub-event-card">
                                    <div className="date-time">
                                        <p>
                                            <FaCalendar />
                                            {new Date(subEvent.date).toLocaleDateString('tr-TR')}
                                        </p>
                                        <p>
                                            <FaClock />
                                            {new Date(subEvent.date).toLocaleTimeString('tr-TR', {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                    <div className="venue-info">
                                        <p>
                                            <FaMapMarkerAlt />
                                            {subEvent.location?.venue}
                                        </p>
                                        {subEvent.location?.city && (
                                            <p>
                                                <FaCity />
                                                {subEvent.location.city}
                                            </p>
                                        )}
                                    </div>
                                    <button 
                                        className="buy-tickets-btn"
                                        onClick={() => navigate(`/seating/${event.id}/${subEvent.id}`)}
                                    >
                                        Bilet Al
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-sub-events">
                            <p>Bu etkinlik için henüz gösterim tarihi belirlenmemiş.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventPage;
